// ISS visible-pass prediction.
//
// "Visible" is not "above the horizon" — three things must hold at the same
// instant: (a) the ISS is above the observer's local horizon, (b) the observer
// is in twilight/darkness so the sky itself is dark, and (c) the ISS is still
// sunlit rather than sitting in Earth's shadow. Skipping (c) is the standard
// way a naive version of this feature ships broken: for a big chunk of every
// orbit the ISS is above the horizon at dawn/dusk but already eclipsed, and
// would report a "pass" that's actually invisible.
import * as satellite from 'satellite.js';
import { sunGeocentric, equatorialToEci } from './celestial.js';

const EARTH_RADIUS_KM = 6371;
// Civil twilight (sun 6° below the horizon) or darker — the common threshold
// used by visible-pass calculators, since the ISS is bright enough to show up
// against a still-lit sky, but the horizon needs to have stopped glowing.
const SUN_ALT_TWILIGHT_DEG = -6;
const COARSE_STEP_MS = 30_000;
const FINE_STEP_MS = 2_000;
const LOOKAHEAD_DAYS = 10;

const COMPASS = [
  'N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE',
  'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW',
];

function compassFor(azimuthDeg) {
  return COMPASS[Math.round(azimuthDeg / 22.5) % 16];
}

// Cylindrical Earth-shadow model: standard simplification for LEO visibility
// checks. The umbra/penumbra cone taper is negligible at ISS altitude (~400km)
// against Earth's own 6371km radius, so a flat cylinder behind the Earth,
// aligned with the anti-solar direction, is accurate enough to decide sunlit
// vs. eclipsed.
function isEclipsed(satEciKm, sunEciKm) {
  const sunDist = Math.hypot(sunEciKm.x, sunEciKm.y, sunEciKm.z);
  const ux = sunEciKm.x / sunDist;
  const uy = sunEciKm.y / sunDist;
  const uz = sunEciKm.z / sunDist;
  const along = satEciKm.x * ux + satEciKm.y * uy + satEciKm.z * uz;
  if (along > 0) return false; // on the sunward side of Earth — always lit
  const satDistSq = satEciKm.x ** 2 + satEciKm.y ** 2 + satEciKm.z ** 2;
  const perpSq = satDistSq - along * along;
  return perpSq < EARTH_RADIUS_KM * EARTH_RADIUS_KM;
}

// Sun altitude (degrees) as seen from the observer — geocentric ra/dec is
// used directly with no topocentric parallax correction, since the Sun's
// parallax is sub-arcminute and irrelevant to a twilight threshold.
function sunAltitudeDeg(sunRaDec, observerRad, gmst) {
  const lst = gmst + observerRad.longitude;
  const hourAngle = lst - sunRaDec.ra;
  const sinAlt =
    Math.sin(sunRaDec.dec) * Math.sin(observerRad.latitude) +
    Math.cos(sunRaDec.dec) * Math.cos(observerRad.latitude) * Math.cos(hourAngle);
  return (Math.asin(Math.max(-1, Math.min(1, sinAlt))) * 180) / Math.PI;
}

function sample(satrec, date, observerGd) {
  const gmst = satellite.gstime(date);
  const pv = satellite.propagate(satrec, date);
  if (!pv.position) return null;

  const ecf = satellite.eciToEcf(pv.position, gmst);
  const look = satellite.ecfToLookAngles(observerGd, ecf);
  const elevationDeg = (look.elevation * 180) / Math.PI;
  const azimuthDeg = ((look.azimuth * 180) / Math.PI + 360) % 360;

  const sun = sunGeocentric(date);
  const sunEci = equatorialToEci(sun.ra, sun.dec, sun.distKm);
  const eclipsed = isEclipsed(pv.position, sunEci);
  const sunAltDeg = sunAltitudeDeg(sun, observerGd, gmst);

  const visible = elevationDeg > 0 && !eclipsed && sunAltDeg < SUN_ALT_TWILIGHT_DEG;
  return { date, elevationDeg, azimuthDeg, visible };
}

function refineWindow(satrec, observerGd, startMs, endMs) {
  let start = null;
  let end = null;
  let max = null;
  for (let t = startMs; t <= endMs; t += FINE_STEP_MS) {
    const s = sample(satrec, new Date(t), observerGd);
    if (!s || !s.visible) continue;
    if (!start) start = s;
    end = s;
    if (!max || s.elevationDeg > max.elevationDeg) max = s;
  }
  if (!start || !end || !max) return null;
  return {
    start: { time: start.date, azimuthDeg: start.azimuthDeg, compass: compassFor(start.azimuthDeg) },
    max: {
      time: max.date,
      elevationDeg: max.elevationDeg,
      azimuthDeg: max.azimuthDeg,
      compass: compassFor(max.azimuthDeg),
    },
    end: { time: end.date, azimuthDeg: end.azimuthDeg, compass: compassFor(end.azimuthDeg) },
    durationSec: Math.round((end.date - start.date) / 1000),
  };
}

/**
 * Computes upcoming genuinely-visible ISS (or any TLE-tracked satellite)
 * passes for an observer over the next `opts.days` (default 10). Note: TLE
 * accuracy for a drag-affected LEO object like the ISS degrades over a ~10
 * day span — this matches the requested v1 lookahead, not a precision claim.
 */
export function computeVisiblePasses(tleLine1, tleLine2, observer, opts = {}) {
  const satrec = satellite.twoline2satrec(tleLine1, tleLine2);
  const observerGd = {
    latitude: (observer.lat * Math.PI) / 180,
    longitude: (observer.lng * Math.PI) / 180,
    height: observer.heightKm ?? 0.2,
  };

  const startMs = (opts.from || new Date()).getTime();
  const endMs = startMs + (opts.days ?? LOOKAHEAD_DAYS) * 86_400_000;

  const passes = [];
  let inWindow = false;
  let windowStart = null;
  let windowEnd = null;

  for (let t = startMs; t <= endMs; t += COARSE_STEP_MS) {
    const s = sample(satrec, new Date(t), observerGd);
    const visible = Boolean(s && s.visible);
    if (visible) {
      if (!inWindow) {
        inWindow = true;
        windowStart = Math.max(startMs, t - COARSE_STEP_MS);
      }
      windowEnd = t + COARSE_STEP_MS;
    } else if (inWindow) {
      const pass = refineWindow(satrec, observerGd, windowStart, windowEnd);
      if (pass) passes.push(pass);
      inWindow = false;
    }
  }
  if (inWindow) {
    const pass = refineWindow(satrec, observerGd, windowStart, windowEnd);
    if (pass) passes.push(pass);
  }
  return passes;
}
